<template>
  <div>
    <PageTitle :breadcrumbName="breadcrumbName" :pageTitle="pageTitle" class="mb-2" />

    <div class="card columns gap2 mb-1">
      <div class="span-12">
        <h5 class="mb-1">LISTAGEM DE CONTATOS</h5>
        <p class="text-light text-sm">
          Quantidade de contatos: <strong>{{ contacts.length }}</strong>
        </p>
      </div>

      <div class="span-12 columns gap2 ai-c">
        <div class="span-4">
          <div class="contacts-filter-field">
            <input
              type="text"
              class="contacts-filter-input-native"
              placeholder="Buscar por nome ou e-mail"
              v-model="filters.search"
              @keyup.enter="loadContacts"
            />
          </div>
        </div>
        <div class="span-3">
          <Select
            :value="filters.companyId"
            :options="companyFilterOptions"
            title="Empresa"
            @input="filters.companyId = $event"
          />
        </div>
        <div class="span-5 flex ai-c fgap2 fw-w">
          <a class="btn solid danger" href="#" @click.prevent="clearFilters">LIMPAR</a>
          <a class="btn solid primary" href="#" @click.prevent="loadContacts">BUSCAR</a>
          <a
            v-if="hasAnyPortalAdmin"
            class="btn solid success"
            href="#"
            @click.prevent="openCreateModal"
          >ADICIONAR</a>
        </div>
      </div>
    </div>

    <div class="card columns gap2 mb-2">
      <div class="span-12 contacts-table-wrapper">
        <Table
          :columns="columns"
          :data="contacts"
          :actions="false"
          :useFilter="false"
          :tdAuto="true"
          tableClass="vgt-table center text-sm text-table text-medium sm-pd contacts-table"
          :trAction="() => {}"
        >
        <template #name="{ item }">
          {{ item.name }}
        </template>
        <template #email1="{ item }">
          {{ item.email1 }}
        </template>
        <template #companyName="{ item }">
          {{ item.companyName }}
        </template>
        <template #actions="{ item }">
          <div v-if="canManageContact(item)" class="contacts-actions">
            <button
              type="button"
              class="btn solid primary small"
              @click.stop.prevent="openEditModal(item)"
              :disabled="!canUnlinkContact(item)"
            >
              Editar
            </button>
            <button
              type="button"
              class="btn solid danger small"
              :disabled="!canUnlinkContact(item)"
              :title="!canUnlinkContact(item) ? 'Você não pode se desvincular das empresas às quais pertence.' : ''"
              @click.stop.prevent="confirmUnlinkContact(item)"
            >
              Desvincular
            </button>
          </div>
        </template>
      </Table>
      </div>
    </div>

    <ContactModal
      :handler="modalOpen"
      :mode="modalMode"
      :contact="selectedContact"
      :companies="companyOptions"
      :menu-permission-options="menuPermissionOptions"
      :report-field-options="reportFieldOptions"
      :reports-permission-id="reportsPermissionId"
      @request-close="closeModal"
      @saved="loadContacts"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import api from '@/services/api';
import PageTitle from '@/components/PageTitle.vue';
import Select from '@/components/DataInput/Select.vue';
import ContactModal from '@/components/ContactModal.vue';
import Table from '@/components/Table.vue';
import columns from '@/utils/ContactTableColumns';
import { REPORTS_MODULE_PERMISSION_CODE } from '@/constants/portalPermissions';

const qs = require('qs');

export default {
  name: 'Contacts',
  components: {
    PageTitle,
    Select,
    Table,
    ContactModal,
  },
  props: {
    pageTitle: {
      type: String,
      default: 'Contatos',
    },
    breadcrumbName: {
      type: String,
      default: 'Gerenciar Contatos',
    },
  },
  data() {
    return {
      columns,
      contacts: [],
      filters: {
        search: '',
        companyId: 'all',
      },
      modalOpen: false,
      modalMode: 'create',
      selectedContact: null,
      menuPermissionOptions: [],
      reportFieldOptions: [],
      reportsPermissionId: null,
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['adminCompanyIds', 'hasAnyPortalAdmin', 'isPortalAdmin']),
    companyOptions() {
      const adminIds = this.adminCompanyIds;

      if (this.user && this.user.companies) {
        return this.user.companies
          .filter((company) => company.customer && !company.customBroker && adminIds.includes(company.id))
          .map((company) => ({
            label: company.name,
            value: company.id,
          }));
      }

      const stored = localStorage.getItem('@GSCMX-PC:companies');
      if (stored) {
        return JSON.parse(stored)
          .filter((company) => adminIds.includes(company.id))
          .map((company) => ({
            label: company.name,
            value: company.id,
          }));
      }

      return [];
    },
    companyFilterOptions() {
      return [
        { label: 'Todas', value: 'all' },
        ...this.companyOptions,
      ];
    },
  },
  async mounted() {
    await Promise.all([
      this.loadContacts(),
      this.loadPermissionsCatalog(),
    ]);
  },
  methods: {
    ...mapActions(['toggleLoading']),
    mapPermissionOptions(permissions) {
      return (permissions || []).map((permission) => ({
        label: permission.description,
        value: permission.id,
      }));
    },
    async loadPermissionsCatalog() {
      try {
        const [moduleResponse, fieldResponse] = await Promise.all([
          api.get('/customers-portal/permissions', { params: { type: 'MODULE_PERMISSION' } }),
          api.get('/customers-portal/permissions', { params: { type: 'FIELD_PERMISSION' } }),
        ]);

        const modulePermissions = moduleResponse.data.data || [];
        const fieldPermissions = fieldResponse.data.data || [];

        this.menuPermissionOptions = this.mapPermissionOptions(modulePermissions);
        this.reportFieldOptions = this.mapPermissionOptions(fieldPermissions);

        const reportsPermission = modulePermissions.find(
          (permission) => permission.code === REPORTS_MODULE_PERMISSION_CODE,
        );
        this.reportsPermissionId = reportsPermission ? reportsPermission.id : null;
      } catch (error) {
        const message = error.response?.data?.message || 'Não foi possível carregar as permissões.';
        this.$toast.error(message, { position: 'top-center' });
        this.menuPermissionOptions = [];
        this.reportFieldOptions = [];
        this.reportsPermissionId = null;
      }
    },
    async loadContacts() {
      if (!this.hasAnyPortalAdmin) {
        this.contacts = [];
        return;
      }

      this.toggleLoading(true);

      try {
        const params = {};

        if (this.filters.search && this.filters.search.trim()) {
          params.search = this.filters.search.trim();
        }

        if (this.filters.companyId && this.filters.companyId !== 'all') {
          if (!this.isPortalAdmin(this.filters.companyId)) {
            this.$toast.error('Você não tem permissão para listar contatos desta empresa.', { position: 'top-center' });
            this.contacts = [];
            return;
          }

          params.companyId = this.filters.companyId;
        }

        const query = Object.keys(params).length ? `?${qs.stringify(params)}` : '';
        const response = await api.get(`/customers/contacts${query}`);

        const data = response.data.data || [];
        this.contacts = data
          .filter((contact) => this.isPortalAdmin(contact.companyId))
          .map((contact) => ({
            id: `${contact.id}-${contact.companyId}`,
            contactId: contact.id,
            name: contact.name || '-',
            email1: contact.email1 || '-',
            companyName: contact.companyName || '-',
            companyId: contact.companyId,
          }));
      } catch (error) {
        const message = error.response?.data?.message || 'Não foi possível carregar os contatos.';
        this.$toast.error(message, { position: 'top-center' });
        this.contacts = [];
      } finally {
        this.toggleLoading(false);
      }
    },
    clearFilters() {
      this.filters = {
        search: '',
        companyId: 'all',
      };
      this.loadContacts();
    },
    openCreateModal() {
      if (!this.hasAnyPortalAdmin) {
        return;
      }

      this.modalMode = 'create';
      this.selectedContact = null;
      this.modalOpen = true;
    },
    async openEditModal(contact) {
      if (!this.canManageContact(contact)) {
        this.$toast.error('Você não tem permissão para editar contatos desta empresa.', { position: 'top-center' });
        return;
      }

      this.toggleLoading(true);

      try {
        const response = await api.get(`/customers/contacts/${contact.contactId}`, {
          params: { companyId: contact.companyId },
        });

        const contactData = response.data.data;

        this.modalMode = 'edit';
        this.selectedContact = {
          id: contactData.id,
          name: contactData.name || '',
          email1: contactData.email1 || '',
          companyId: contactData.companyId,
          companyName: contactData.companyName || '',
          portalAdmin: Boolean(contactData.portalAdmin),
          permissions: contactData.permissions ? [...contactData.permissions] : [],
          reportFields: contactData.reportFields ? [...contactData.reportFields] : [],
        };
        this.modalOpen = true;
      } catch (error) {
        const message = error.response?.data?.message || 'Não foi possível carregar o contato.';
        this.$toast.error(message, { position: 'top-center' });
      } finally {
        this.toggleLoading(false);
      }
    },
    closeModal() {
      this.modalOpen = false;
      this.selectedContact = null;
    },
    canManageContact(contact) {
      return this.isPortalAdmin(contact.companyId);
    },
    canUnlinkContact(contact) {
      if (!this.user || !this.user.id) {
        return true;
      }

      return contact.contactId !== this.user.id;
    },
    async confirmUnlinkContact(contact) {
      if (!this.canManageContact(contact)) {
        this.$toast.error('Você não tem permissão para desvincular contatos desta empresa.', { position: 'top-center' });
        return;
      }

      if (!this.canUnlinkContact(contact)) {
        this.$toast.error('Você não pode se desvincular das empresas às quais pertence.', { position: 'top-center' });
        return;
      }

      const { isConfirmed } = await this.$swal.fire({
        title: 'Desvincular contato',
        html: `<p>Deseja realmente desvincular <strong>${contact.name}</strong> da empresa <strong>${contact.companyName}</strong>?</p>`,
        icon: 'warning',
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonColor: 'var(--primary-color)',
        confirmButtonText: 'Desvincular',
        cancelButtonText: 'Cancelar',
      });

      if (!isConfirmed) {
        return;
      }

      this.toggleLoading(true);

      try {
        await api.delete(`/customers/contacts/${contact.contactId}`, {
          params: { companyId: contact.companyId },
        });

        this.$toast.success('Contato desvinculado com sucesso.', { position: 'top-center' });
        await this.loadContacts();
      } catch (error) {
        const message = error.response?.data?.message || 'Não foi possível desvincular o contato.';
        this.$toast.error(message, { position: 'top-center' });
      } finally {
        this.toggleLoading(false);
      }
    },
  },
};
</script>

<style scoped>
.contacts-table-wrapper {
  width: 100%;
}

.contacts-table-wrapper >>> table {
  width: 100%;
  table-layout: fixed;
}

.contacts-table-wrapper >>> th,
.contacts-table-wrapper >>> td {
  max-width: none !important;
  white-space: normal;
  word-break: break-word;
}

.contacts-table-wrapper >>> th:nth-child(1),
.contacts-table-wrapper >>> td:nth-child(1) {
  width: 22%;
}

.contacts-table-wrapper >>> th:nth-child(2),
.contacts-table-wrapper >>> td:nth-child(2) {
  width: 28%;
}

.contacts-table-wrapper >>> th:nth-child(3),
.contacts-table-wrapper >>> td:nth-child(3) {
  width: 35%;
}

.contacts-table-wrapper >>> th:nth-child(4),
.contacts-table-wrapper >>> td:nth-child(4) {
  width: 20%;
}

.contacts-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.contacts-actions .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.contacts-filter-field {
  border: var(--small-border-width) solid var(--primary-color);
  height: 50px;
  border-radius: var(--round-border-radius);
  padding: 0.3rem 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.contacts-filter-field .title {
  font-weight: 700;
  font-size: 0.8rem;
  color: var(--primary-color);
  margin: 0;
  line-height: 1;
}

.contacts-filter-input-native,
.contacts-filter-field .contacts-filter-input-native:hover,
.contacts-filter-field .contacts-filter-input-native:focus,
.contacts-filter-field .contacts-filter-input-native:focus-visible {
  border: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
  color: var(--primary-color);
  padding: 0;
  width: 100%;
  height: auto;
  min-height: 0;
  border-radius: 0;
}

.contacts-filter-input-native::placeholder {
  color: rgba(9, 35, 61, 0.45);
}
</style>
